import React, { useState, useEffect, useCallback } from 'react';
import { useQobuzStore } from '../lib/useQobuz';
import { QobuzIcon } from './ServiceIcons';
import { isIosShell } from '../utils/platform';

const GOLD = '#C9A84C';
const PREVIEW_COUNT_KEY = 'maestro_preview_play_count';
const DISMISSED_KEY = 'maestro_qobuz_modal_dismissed';
const COOLDOWN_MS = 5 * 60 * 1000;

function getPreviewCount() {
  try { return parseInt(sessionStorage.getItem(PREVIEW_COUNT_KEY) || '0', 10); } catch { return 0; }
}

export function incrementPreviewCount() {
  try {
    const count = getPreviewCount() + 1;
    sessionStorage.setItem(PREVIEW_COUNT_KEY, String(count));
    if (count >= 2) {
      window.dispatchEvent(new CustomEvent('maestro:show-qobuz-modal'));
    }
  } catch {}
}

export function resetPreviewCount() {
  try { sessionStorage.removeItem(PREVIEW_COUNT_KEY); } catch {}
}

export default function QobuzSubscribeModal() {
  const [show, setShow] = useState(false);
  const [visible, setVisible] = useState(false);
  const qobuzConnected = useQobuzStore((s) => s.connected);
  const qobuzLoginOAuth = useQobuzStore((s) => s.loginOAuth);
  const qobuzConnecting = useQobuzStore((s) => s.connecting);
  const qobuzUser = useQobuzStore((s) => s.user);
  const qobuzError = useQobuzStore((s) => s.error);

  const dismiss = useCallback(() => {
    setVisible(false);
    try { sessionStorage.setItem(DISMISSED_KEY, String(Date.now())); } catch {}
    setTimeout(() => setShow(false), 300);
  }, []);

  useEffect(() => {
    const handler = () => {
      try {
        const dismissed = sessionStorage.getItem(DISMISSED_KEY);
        if (dismissed && Date.now() - parseInt(dismissed, 10) < COOLDOWN_MS) return;
      } catch {}

      setShow(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
    };

    window.addEventListener('maestro:show-qobuz-modal', handler);
    return () => window.removeEventListener('maestro:show-qobuz-modal', handler);
  }, [show, dismiss]);

  const handleOAuthLogin = () => {
    qobuzLoginOAuth();
  };

  useEffect(() => {
    if (qobuzConnected && show) {
      resetPreviewCount();
      dismiss();
    }
  }, [qobuzConnected, show, dismiss]);

  if (!show) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div
        onClick={dismiss}
        style={{
          position: 'absolute', inset: 0,
          background: 'rgba(0,0,0,0.55)',
          backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
          opacity: visible ? 1 : 0,
          transition: 'opacity 0.3s ease',
        }}
      />
      <div style={{
        position: 'relative', width: '90%', maxWidth: 400,
        background: 'var(--bg-primary)',
        borderRadius: 20, padding: '28px 24px',
        boxShadow: '0 24px 48px rgba(0,0,0,0.3)',
        border: '1px solid var(--border-light)',
        transform: visible ? 'scale(1) translateY(0)' : 'scale(0.95) translateY(20px)',
        opacity: visible ? 1 : 0,
        transition: 'transform 0.35s cubic-bezier(0.32, 0.72, 0, 1), opacity 0.3s ease',
      }}>
        <button
          onClick={dismiss}
          style={{
            position: 'absolute', top: 12, right: 12,
            width: 32, height: 32, borderRadius: '50%',
            background: 'var(--bg-secondary)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--text-secondary)', cursor: 'pointer',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <span style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <QobuzIcon size={36} />
          </span>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
              Unlock Full Tracks
            </h2>
            <p style={{ fontSize: 13, color: 'var(--text-tertiary)', margin: '2px 0 0' }}>
              You're hearing 30-second previews
            </p>
          </div>
        </div>

        <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 20 }}>
          {qobuzConnected
            ? 'Your Qobuz session may have expired. Please sign in again to restore full-quality streaming.'
            : 'Connect your Qobuz account to enjoy full-length, high-resolution streaming. Already have a subscription? Sign in below.'}
        </p>

        <button
          onClick={handleOAuthLogin}
          disabled={qobuzConnecting}
          style={{
            width: '100%', padding: '12px 0', borderRadius: 10, fontSize: 14, fontWeight: 700,
            background: GOLD, color: '#1a1a2e',
            cursor: qobuzConnecting ? 'wait' : 'pointer',
            opacity: qobuzConnecting ? 0.6 : 1,
            transition: 'opacity 0.15s',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            border: 'none',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><polyline points="10 17 15 12 10 7" /><line x1="15" y1="12" x2="3" y2="12" />
          </svg>
          {qobuzConnecting ? 'Connecting…' : 'Sign in with Qobuz'}
        </button>
        {qobuzError && (
          <p style={{ fontSize: 12, color: '#ef4444', textAlign: 'center', margin: '8px 0 0' }}>{qobuzError}</p>
        )}

        {!isIosShell() && (
        <div data-testid="qobuz-signup-link-block" style={{
          marginTop: 16, paddingTop: 16,
          borderTop: '1px solid var(--border-light)', textAlign: 'center',
        }}>
          <p style={{ fontSize: 13, color: 'var(--text-tertiary)', margin: 0, lineHeight: 1.6 }}>Don't have a Qobuz account?</p>
          <a
            href="https://www.qobuz.com/us-en/discover"
            target="_blank" rel="noopener noreferrer"
            style={{
              display: 'inline-block', marginTop: 8,
              fontSize: 13, fontWeight: 600, color: GOLD,
              textDecoration: 'none',
            }}
          >
            Sign up at Qobuz →
          </a>
        </div>
        )}
      </div>
    </div>
  );
}
